<template>
  <v-dialog :value="open" max-width="720" persistent scrollable>
    <v-card class="rounded-lg">
      <v-toolbar color="success" dark flat>
        <v-toolbar-title>
          <v-icon left>mdi-file-excel</v-icon>
          Import khách hàng và đơn hàng
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon :disabled="loading" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="pa-5">
        <v-alert type="info" text dense class="mb-5">
          Mỗi dòng Excel là một đơn hàng. Hệ thống đọc sản phẩm ở cột C,
          ngày mua ở cột B, số tiền đã thanh toán ở cột F và thông tin khách hàng ở cột Q.
        </v-alert>

        <v-form ref="form" v-model="valid">
          <v-select
            v-model="dataType"
            :items="dataTypeOptions"
            label="Tình trạng khách hàng khi nhập"
            prepend-icon="mdi-account-tag"
            outlined
            :rules="[v => !!v || 'Vui lòng chọn phân loại data']"
          ></v-select>

          <v-select
            v-model="customerSourceId"
            :items="customerSources"
            item-text="name"
            item-value="id"
            label="Nguồn khách hàng"
            prepend-icon="mdi-source-branch"
            outlined
            :loading="sourcesLoading"
            :rules="[v => !!v || 'Vui lòng chọn nguồn khách hàng']"
          ></v-select>

          <v-file-input
            v-model="file"
            label="Chọn file Excel"
            prepend-icon="mdi-microsoft-excel"
            accept=".xlsx,.xls,.csv"
            outlined
            show-size
            :rules="[v => !!v || 'Vui lòng chọn file Excel']"
          ></v-file-input>
        </v-form>

        <v-simple-table dense class="mb-4">
          <thead>
            <tr>
              <th>Cột</th>
              <th>Nội dung</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>C</strong></td><td>Sản phẩm; dùng x2, x3... để chỉ số lượng</td></tr>
            <tr><td><strong>B</strong></td><td>Ngày mua hàng</td></tr>
            <tr><td><strong>F</strong></td><td>Số tiền khách hàng đã thanh toán</td></tr>
            <tr><td><strong>Q</strong></td><td>Tên, số điện thoại và địa chỉ (mỗi phần một dòng)</td></tr>
          </tbody>
        </v-simple-table>

        <v-alert v-if="result" :type="result.errors.length ? 'warning' : 'success'" text>
          <div><strong>Đã import {{ result.imported_rows }} dòng</strong></div>
          <div>Tạo mới {{ result.created_customers }} khách hàng và {{ result.created_orders }} đơn hàng.</div>
          <v-btn
            v-if="result.history_id"
            small
            color="primary"
            class="mt-3"
            :loading="downloadingId === result.history_id"
            @click="downloadReport(result.history_id)"
          >
            <v-icon left small>mdi-download</v-icon>
            Tải file kết quả
          </v-btn>
        </v-alert>

        <div v-if="result && result.errors.length">
          <div class="font-weight-bold error--text mb-2">
            {{ result.errors.length }} dòng chưa import được:
          </div>
          <v-list dense outlined class="import-error-list">
            <v-list-item v-for="error in result.errors" :key="`${error.row}-${error.message}`">
              <v-list-item-icon class="mr-3">
                <v-icon color="error">mdi-alert-circle-outline</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>Dòng {{ error.row }}</v-list-item-title>
                <v-list-item-subtitle class="text-wrap">{{ error.message }}</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </div>

        <v-divider class="my-5"></v-divider>
        <div class="d-flex align-center mb-2">
          <div class="text-subtitle-1 font-weight-bold">Lịch sử import gần đây</div>
          <v-spacer></v-spacer>
          <v-btn icon small :loading="historyLoading" @click="fetchHistory">
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
        </div>
        <v-simple-table dense fixed-header height="230" v-if="histories.length">
          <thead>
            <tr>
              <th>Thời gian / File</th>
              <th>Kết quả</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="history in histories" :key="history.id">
              <td>
                <div class="font-weight-medium">{{ history.original_filename }}</div>
                <div class="text-caption grey--text">{{ formatDate(history.created_at) }}</div>
              </td>
              <td>
                <v-chip x-small color="success" dark>{{ history.successful_rows }} thành công</v-chip>
                <v-chip x-small color="error" dark class="ml-1">{{ history.failed_rows }} lỗi</v-chip>
              </td>
              <td class="text-right">
                <v-btn
                  icon
                  color="primary"
                  :loading="downloadingId === history.id"
                  @click="downloadReport(history.id)"
                >
                  <v-icon>mdi-file-download-outline</v-icon>
                </v-btn>
              </td>
            </tr>
          </tbody>
        </v-simple-table>
        <div v-else-if="!historyLoading" class="text-center grey--text py-5">
          Chưa có lịch sử import.
        </div>
      </v-card-text>

      <v-divider></v-divider>
      <v-card-actions class="pa-4">
        <v-spacer></v-spacer>
        <v-btn text :disabled="loading" @click="close">Đóng</v-btn>
        <v-btn color="success" dark :loading="loading" @click="submit">
          <v-icon left>mdi-upload</v-icon>
          Bắt đầu import
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import customerModule from '@/api/customers'
import sourceModule from '@/api/customerSources'

const customerActions = customerModule.actions
const sourceActions = sourceModule.actions

export default {
  props: {
    open: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      valid: true,
      loading: false,
      file: null,
      dataType: 'successful',
      dataTypeOptions: [
        { text: 'Đã mua hàng', value: 'successful' },
        { text: 'Khách tiềm năng (chưa mua hàng)', value: 'potential' }
      ],
      customerSourceId: null,
      customerSources: [],
      sourcesLoading: false,
      result: null,
      histories: [],
      historyLoading: false,
      downloadingId: null
    }
  },
  watch: {
    open(value) {
      if (value) {
        this.fetchHistory()
        this.fetchSources()
      }
    }
  },
  methods: {
    close() {
      if (this.loading) return
      this.$emit('toggle')
    },
    submit() {
      if (!this.$refs.form.validate()) return

      const formData = new FormData()
      formData.append('file', this.file)
      formData.append('data_type', this.dataType)
      formData.append('customer_source_id', this.customerSourceId)

      this.loading = true
      this.result = null
      customerActions.importCustomerOrders(formData)
        .then(response => {
          this.result = response.data?.data || response.data
          this.$emit('refresh')
          this.fetchHistory()
          if (!this.result.errors?.length) {
            this.$snackbar.success('Import khách hàng và đơn hàng thành công!')
          }
        })
        .catch(error => {
          const message = error.response?.data?.message || 'Không thể import file Excel.'
          this.$snackbar.error(message)
        })
        .finally(() => {
          this.loading = false
        })
    },
    fetchSources() {
      this.sourcesLoading = true
      sourceActions.getSources()
        .then(response => {
          this.customerSources = response.data?.data || []
          const defaultSource = this.customerSources.find(source => source.is_default)
          if (!this.customerSourceId || !this.customerSources.some(source => source.id === this.customerSourceId)) {
            this.customerSourceId = defaultSource?.id || this.customerSources[0]?.id || null
          }
        })
        .catch(() => {
          this.$snackbar.error('Không thể tải danh sách nguồn khách hàng.')
        })
        .finally(() => {
          this.sourcesLoading = false
        })
    },
    fetchHistory() {
      this.historyLoading = true
      customerActions.getCustomerImportHistory()
        .then(response => {
          this.histories = response.data?.data || []
        })
        .catch(() => {
          this.$snackbar.error('Không thể tải lịch sử import.')
        })
        .finally(() => {
          this.historyLoading = false
        })
    },
    downloadReport(id) {
      this.downloadingId = id
      customerActions.downloadCustomerImportReport(id)
        .then(response => {
          const blob = new Blob([response.data], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          })
          const url = window.URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = url
          link.download = `ket-qua-import-${id}.xlsx`
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          window.URL.revokeObjectURL(url)
        })
        .catch(() => {
          this.$snackbar.error('Không thể tải file kết quả import.')
        })
        .finally(() => {
          this.downloadingId = null
        })
    },
    formatDate(value) {
      return value ? new Date(value).toLocaleString('vi-VN') : ''
    }
  }
}
</script>

<style scoped>
.import-error-list {
  max-height: 240px;
  overflow-y: auto;
}
</style>
